<script lang="ts" setup>
import UserAvatar from "@/components/UserAvatar.vue";
import { Tag } from "vant";
import { computed, nextTick, ref } from "vue";
import { MediaUploadFileItem } from "@/types/component";
import { useMate } from "@/stores/mate";
import { useReply } from "@/stores/reply";
import { useGlobalUser } from "@/stores/globalUser";
import { onMounted } from "vue";
import { Entity, Mate, Moment, Reply } from "@/types/entity";
import { useGlobalModal } from "@/stores/globalModal";
import { AIWriteType } from "@/enums/mateEnums";
import { useUI } from "@/stores/ui";
import { ReplyEntityType } from "@/enums";
import { removeHtmlTag } from "@/tools/text";
import { useI18n } from "vue-i18n";
import { useMoment } from "@/stores/moment";
import { FieldInstance } from "vant/es";

const props = withDefaults(
  defineProps<{
    replyTarget?: Reply | null;
    entityId?: number;
    updateReply?: Reply | null;
    showTip?: boolean;

    entityType?: ReplyEntityType;
    entity?: Entity;
    mate?: Mate;
  }>(),
  {
    showTip: false,
  }
);
const emit = defineEmits<{
  (e: "reply-updated"): void;
  (e: "replyAborted"): void;
}>();

const { t } = useI18n();
const mateStore = useMate();
const user = useGlobalUser();
const momentStore = useMoment();
const showMediaSelect = ref(false);
const replyStore = useReply();
const replyMsg = ref("");
const uploadedImgList = ref<MediaUploadFileItem[]>([]);
const replyAtMates = ref<Pick<Mate, "name" | "uuid">[]>([]);
const isReplayDisabled = ref(false);
const showRemindMates = ref(false);

const deleteAtMate = (mate: Pick<Mate, "name" | "uuid">) => {
  replyAtMates.value = replyAtMates.value.filter(
    (item) => item.uuid != mate.uuid
  );
};
const isTreehole = computed(() => {
  return (
    props.entityType === ReplyEntityType.Moment &&
    (props.entity as Moment).type === 2
  );
});
const clearReplyInput = () => {
  replyMsg.value = "";
  uploadedImgList.value = [];
  replyAtMates.value = [];
};
const onReply = async () => {
  isReplayDisabled.value = true;
  try {
    const params = {
      mateId: mateStore.currentMate!.uuid,
      content: replyMsg.value,
      mentionMateIdzz: replyAtMates.value.map((item) => item.uuid),
      parentId: props.replyTarget!.id!,
      mediaIdzz: uploadedImgList.value.map((item) => item.mediaId),
    };
    if (props.updateReply) {
      await replyStore.updateReply(props.updateReply.id!, params);
      emit("reply-updated");
    } else {
      await replyStore.createReply(params, props.entityId);

      // 清空输入状态
      clearReplyInput();
    }

    emit("replyAborted");
    if (props.entityType === ReplyEntityType.Moment) {
      momentStore.getOrFetchMomentItem((props.entity as Moment).uuid, true);
    }
  } finally {
    isReplayDisabled.value = false;
  }
};

const modalStore = useGlobalModal();
const openRichTextEditor = () => {
  console.log(props.replyTarget?.id);
  modalStore.openRichEditor({
    title: t(`Overview.comments`),
    content: replyMsg.value,
    aiType: AIWriteType.Reply,
    options: {
      entityId: mateStore.currentMate?.id,
      entityType: "mate",
      parentId: props.replyTarget?.id,
      replyEntityId: props.entity?.id,
      replyEntityType: props.entityType,
      mateId: props.mate?.id || mateStore.currentMate?.id,
      recipientId: props.replyTarget?.mate.id,
    },
    callback: (text?: string) => {
      console.log(text);
      replyMsg.value = text!;
    },
  });
};

const uiStore = useUI();
const clickSwitchMate = () => {
  if (props.mate) return;
  uiStore.popMateSelector({
    selectedMateId: mateStore.currentMate?.id,
    callback() {
      uiStore.showMateSelector = false;
    },
  });
};

export interface ReplyInputInstance {
  focus: () => void;
}

const replyInputRef = ref<FieldInstance>();
const focus = () => {
  console.log("focus");
  if (isTreehole.value) {
    nextTick(() => {
      replyInputRef.value?.focus();
    });
  } else {
    nextTick(() => {
      openRichTextEditor();
    });
  }
};

defineExpose({
  focus,
});

onMounted(() => {
  if (!props.updateReply) return;
  const { mediazz, content, mentionMatezz } = props.updateReply;
  replyMsg.value = content;
  uploadedImgList.value = mediazz.map((media) => ({
    mediaId: media.id,
    url: media.url,
  })) as MediaUploadFileItem[];
  replyAtMates.value = mentionMatezz;
});
</script>
<template>
  <div
    v-if="user.isLogin"
    class="flex flex-col gap-1 w-full bg-base-100 p-2 h-full shadow-lg"
  >
    <div v-if="showTip" class="flex justify-between mb-1 items-center">
      <div class="flex text-base-content/50 gap-1">
        <div class="shrink-0">
          {{ `${$t("Reply.reply")} ${replyTarget?.mate.name}:` }}
        </div>
        <div class="line-clamp-1">{{ replyTarget?.content }}</div>
      </div>
      <XmarkCircleIcon
        class="icon-base cursor-pointer shrink-0"
        @click="emit('replyAborted')"
      />
    </div>
    <div
      v-if="replyAtMates.length > 0"
      class="flex gap-1 flex-wrap border-b pb-1"
    >
      <Tag
        v-for="atMate in replyAtMates"
        :key="atMate.uuid"
        class="badge badge-primary badge-sm"
        closeable
        @click="deleteAtMate(atMate)"
        @close="deleteAtMate(atMate)"
      >
        {{ atMate.name }}
      </Tag>
    </div>
    <div class="flex items-center justify-between flex-1">
      <div class="flex items-end gap-2 flex-grow">
        <div v-if="!isTreehole" class="relative" @click="clickSwitchMate">
          <user-avatar
            :mate="mate || mateStore.currentMate!"
            class="h-11 flex-shrink-0"
          />

          <div
            v-if="!mate"
            class="absolute -bottom-1 -right-1 rounded-full bg-base-100 flex items-center justify-center p-1"
          >
            <i class="iconify mingcute--transfer-2-line w-4 h-4" />
          </div>
        </div>
        <div class="flex items-end flex-grow rounded pr-2">
          <div
            class="flex-grow flex min-h-[2.75rem] rounded max-h-[44px] overflow-hidden"
          >
            <div class="flex flex-col flex-grow pl-3 mr-2 overflow-y-scroll">
              <div
                class="flex-grow flex items-center"
                @click="openRichTextEditor"
                v-if="!isTreehole"
              >
                <div
                  v-if="removeHtmlTag(replyMsg)"
                  class="prose prose-sm same-size break-all"
                  v-html="replyMsg"
                />
                <button v-else class="btn btn-xs w-full">
                  {{
                    replyTarget?.mate
                      ? `${$t("Reply.reply")}@${replyTarget.mate.name}`
                      : $t("Reply.enterYourReply")
                  }}
                </button>
              </div>
              <VanField
                v-else
                ref="replyInputRef"
                v-model="replyMsg"
                :disabled="isReplayDisabled"
                :placeholder="
                  replyTarget?.mate
                    ? `${$t('Reply.reply')}@${replyTarget.mate.name}`
                    : $t('Reply.enterYourReply')
                "
                autosize
                class="px-0 py-1 after:hidden bg-transparent"
                rows="1"
                type="textarea"
              />
            </div>
          </div>
          <div class="flex flex-col justify-end h-11">
            <div class="flex items-center flex-shrink-0 h-full justify-end">
              <button
                v-if="!isTreehole"
                class="btn btn-xs btn-ghost"
                @click="showRemindMates = true"
              >
                <at-icon :class="{ 'text-primary': replyAtMates.length > 0 }" />
              </button>
              <!-- <button
                :disabled="uploadedImgList.length > 0"
                class="btn btn-xs btn-ghost"
                @click="showMediaSelect = true"
              >
                <i class="iconify mingcute--pic-line" />
              </button> -->
              <button
                :disabled="
                  (!removeHtmlTag(replyMsg) && uploadedImgList.length === 0) ||
                  isReplayDisabled
                "
                class="btn btn-xs rounded btn-primary"
                @click="onReply"
              >
                <span
                  v-if="isReplayDisabled"
                  class="loading loading-ring"
                ></span>
                <span v-else>
                  {{ updateReply ? $t(`Buttons.save`) : $t("Reply.reply") }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <RemindMates
      v-model:at-mate-ids="replyAtMates"
      v-model:show="showRemindMates"
    />
    <media-select-popup
      v-model:media-list="uploadedImgList"
      v-model:show="showMediaSelect"
      :max-count="1"
      :multiple="false"
      :upload-type="6"
      media-type="img"
      replace
    />
  </div>
  <div
    v-else
    class="flex w-full bg-base-100 border-t-neutral border-t p-2 h-full"
  >
    <button class="btn btn-neutral w-full" @click="user.loginModal.show = true">
      {{ $t("Buttons.loginToReply") }}
    </button>
  </div>
</template>
